import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ApiRoutes from '../config/api.routes';
import useAuthorization from './useAuthorization';
import { useAxios } from '../services/http.service';
import { EcoTokenStatus } from '../utils/constants';

const mergeUniqueTokens = (primaryTokens: any[] = [], secondaryTokens: any[] = []) => {
    const mergedTokens: any[] = [];
    const seenIds = new Set<string | number>();

    [...primaryTokens, ...secondaryTokens].forEach((token) => {
        const tokenId = token?.id ?? token?.tokenId;

        if (tokenId === undefined || tokenId === null) {
            mergedTokens.push(token);
            return;
        }

        if (seenIds.has(tokenId)) {
            return;
        }

        seenIds.add(tokenId);
        mergedTokens.push(token);
    });

    return mergedTokens;
};

interface ProfileCacheData {
    walletData: any;
    trusteeTokens: any;
    boughtLands: any;
    mintedTokens: any;
    profileTokens: any;
    adminOrganizations: any;
    unverifiedTrusts: any;
    adminTrustAdmins: any;
    verifiedBy: any;
    complaintsData: any;
    trustTokens: any;
    trustVerifiedLands: any;
    trustUnverifiedLands: any;
    transactionData: any;
    lastFetched: number | null;
    isLoading: boolean;
    hasError: boolean;
}

interface ProfileDataCacheContextType {
    profileCache: ProfileCacheData;
    // `silent` refetches without flipping isLoading, so tables keep their rows on screen
    // instead of swapping to skeletons (e.g. the refresh after a TR3 claim). Default is loud.
    refreshProfileData: (options?: { silent?: boolean }) => Promise<void>;
    clearCache: () => void;
    getCachedData: (dataType: keyof Omit<ProfileCacheData, 'lastFetched' | 'isLoading' | 'hasError'>) => any;
}

const ProfileDataCacheContext = createContext<ProfileDataCacheContextType | undefined>(undefined);

// Cache expiry time (5 minutes)
const CACHE_EXPIRY_TIME = 5 * 60 * 1000;

const initialCacheState: ProfileCacheData = {
    walletData: null,
    trusteeTokens: null,
    boughtLands: null,
    mintedTokens: null,
    profileTokens: null,
    adminOrganizations: null,
    unverifiedTrusts: null,
    adminTrustAdmins: null,
    verifiedBy: null,
    complaintsData: null,
    trustTokens: null,
    trustVerifiedLands: null,
    trustUnverifiedLands: null,
    transactionData: null,
    lastFetched: null,
    isLoading: false,
    hasError: false,
};

export const ProfileDataCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isTrust } = useAuthorization();
    const axios = useAxios();
    const [profileCache, setProfileCache] = useState<ProfileCacheData>(initialCacheState);
    const [previousUserAddress, setPreviousUserAddress] = useState<string | null>(null);
    const [isFetching, setIsFetching] = useState(false);

    useEffect(() => {
        const currentAddress = user?.address;

        // If user address changed to a different address (account switching)
        if (previousUserAddress && currentAddress && previousUserAddress !== currentAddress) {
            console.log('ProfileDataCache: User address changed, clearing cache and fetching new data', {
                previousAddress: previousUserAddress,
                newAddress: currentAddress
            });
            setProfileCache(initialCacheState); // Clear the cache
            fetchAllProfileData(); // Fetch new data
        }
        // If user logged in for the first time or cache is empty
        else if (currentAddress && !profileCache.lastFetched) {
            console.log('ProfileDataCache: User detected, fetching initial data');
            fetchAllProfileData();
        }
        // If user logged out
        else if (!currentAddress && previousUserAddress) {
            console.log('ProfileDataCache: User logged out, clearing cache');
            setProfileCache(initialCacheState);
        }

        // Update previous address
        setPreviousUserAddress(currentAddress || null);
    }, [user?.address]);

    const fetchAllProfileData = useCallback(async (silent = false) => {
        if (!user?.address || isFetching) {
            return;
        }

        setIsFetching(true);
        if (!silent) {
            setProfileCache((prev) => ({ ...prev, isLoading: true, hasError: false }));
        }

        try {
            console.log('Fetching all profile data...');
            const promises = [];

            // Always fetch wallet data
            promises.push(
                axios.get(`${ApiRoutes.user.wallet}?address=${user.address}`)
                    .then((res) => ({ type: 'walletData', data: res.data }))
                    .catch((error) => ({ type: 'walletData', error }))
            );

            // Always fetch trustee tokens
            promises.push(
                axios.get(ApiRoutes.token.trustee(user.address))
                    .then((res) => ({ type: 'trusteeTokens', data: res.data }))
                    .catch((error) => ({ type: 'trusteeTokens', error }))
            );

            // Always fetch bought lands
            promises.push(
                axios.get(`${ApiRoutes.token.list}?boughtBy=${user.address}`)
                    .then((res) => ({ type: 'boughtLands', data: res.data }))
                    .catch((error) => ({ type: 'boughtLands', error }))
            );

            // Always fetch minted tokens
            promises.push(
                axios.get(ApiRoutes.token.minted(user.address))
                    .then((res) => ({ type: 'mintedTokens', data: res.data }))
                    .catch((error) => ({ type: 'mintedTokens', error }))
            );

            // Always fetch profile tokens
            promises.push(
                axios.get(ApiRoutes.user.profile.tokens.root)
                    .then((res) => ({ type: 'profileTokens', data: res.data }))
                    .catch((error) => ({ type: 'profileTokens', error }))
            );

            // Admin-specific data
            if (user.role === 'admin') {
                promises.push(
                    axios.get(ApiRoutes.admin.organizations)
                        .then((res) => ({ type: 'adminOrganizations', data: res.data }))
                        .catch((error) => ({ type: 'adminOrganizations', error }))
                );
                promises.push(
                    axios.get(ApiRoutes.trust.unverified)
                        .then((res) => ({ type: 'unverifiedTrusts', data: res.data }))
                        .catch((error) => ({ type: 'unverifiedTrusts', error }))
                );
                promises.push(
                    axios.get(ApiRoutes.admin.trustAdminsKycVerified)
                        .then((res) => ({ type: 'adminTrustAdmins', data: res.data }))
                        .catch((error) => ({ type: 'adminTrustAdmins', error }))
                );
                promises.push(
                    axios.get(ApiRoutes.admin.verifiedBy(user.address))
                        .then((res) => {
                            const data = res?.data;
                            const normalized = Array.isArray(data) ? data : data?.items || data?.data || [];
                            return { type: 'verifiedBy', data: normalized };
                        })
                        .catch((error) => ({ type: 'verifiedBy', error }))
                );
                promises.push(
                    axios.get(ApiRoutes.trust.complaints.root)
                        .then((res) => ({ type: 'complaintsData', data: res.data }))
                        .catch((error) => ({ type: 'complaintsData', error }))
                );
            }

            // Trust-specific data
            if (isTrust) {
                promises.push(
                    Promise.allSettled([
                        axios.get(ApiRoutes.trust.tokens),
                        axios.get(ApiRoutes.trust.availableTokens),
                    ])
                        .then((results) => {
                            const trustedTokensResponse = results[0];
                            const availableTokensResponse = results[1];

                            const trustedTokens = trustedTokensResponse.status === 'fulfilled' ? trustedTokensResponse.value.data : [];
                            const availableTokens = availableTokensResponse.status === 'fulfilled' ? availableTokensResponse.value.data : [];

                            if (trustedTokensResponse.status === 'rejected' && availableTokensResponse.status === 'rejected') {
                                throw trustedTokensResponse.reason || availableTokensResponse.reason;
                            }

                            return {
                                type: 'trustTokens',
                                data: mergeUniqueTokens(trustedTokens, availableTokens),
                            };
                        })
                        .catch((error) => ({ type: 'trustTokens', error }))
                );
                const verifiedUrl = ApiRoutes.trust.getVerifications +
                    "/?status=" + EcoTokenStatus.APPROVED + "," + EcoTokenStatus.VERIFIED;
                promises.push(
                    axios.get(verifiedUrl)
                        .then((res) => ({ type: 'trustVerifiedLands', data: res.data }))
                        .catch((error) => ({ type: 'trustVerifiedLands', error }))
                );
                const unverifiedUrl = ApiRoutes.trust.getVerifications +
                    "/?status=" + EcoTokenStatus.RE_VERIFICATION_REQUIRED + "," +
                    EcoTokenStatus.CLAIMED + "," + EcoTokenStatus.VERIFICATION_PREPARED;
                promises.push(
                    axios.get(unverifiedUrl)
                        .then((res) => ({ type: 'trustUnverifiedLands', data: res.data }))
                        .catch((error) => ({ type: 'trustUnverifiedLands', error }))
                );
            }

            // Transaction data
            const transactionUrl = ApiRoutes.ecoBank.transactions.byAddress(user.address) +
                `&type=${isTrust ? "user" : "user"}&addToDetails=true`;
            promises.push(
                axios.get(transactionUrl)
                    .then((res) => ({ type: 'transactionData', data: res.data }))
                    .catch((error) => ({ type: 'transactionData', error }))
            );

            const results = await Promise.allSettled(promises);

            const newCacheData: Partial<ProfileCacheData> = {
                lastFetched: Date.now(),
                isLoading: false,
                hasError: false,
            };

            results.forEach((result) => {
                if (result.status === 'fulfilled') {
                    const resultValue = result.value;
                    if ('error' in resultValue) {
                        console.error(`Failed to fetch ${resultValue.type}:`, resultValue.error);
                        newCacheData.hasError = true;
                    } else {
                        newCacheData[resultValue.type as keyof ProfileCacheData] = resultValue.data;
                    }
                } else {
                    console.error('Promise failed:', result.reason);
                    newCacheData.hasError = true;
                }
            });

            setProfileCache((prev) => ({ ...prev, ...newCacheData }));
            console.log('Profile data cache updated successfully');
        } catch (error) {
            console.error('Error fetching profile data:', error);
            setProfileCache((prev) => ({
                ...prev,
                isLoading: false,
                hasError: true,
            }));
        } finally {
            setIsFetching(false);
        }
    }, [user?.address, axios, isTrust]);

    const refreshProfileData = useCallback(async (options?: { silent?: boolean }) => {
        await fetchAllProfileData(!!options?.silent);
    }, [fetchAllProfileData]);

    const clearCache = useCallback(() => {
        setProfileCache(initialCacheState);
        setIsFetching(false);
    }, []);

    const getCachedData = useCallback((dataType: keyof Omit<ProfileCacheData, 'lastFetched' | 'isLoading' | 'hasError'>) => {
        // Access current state values directly - React guarantees these are current
        const currentCache = profileCache;
        const currentIsFetching = isFetching;

        // Check if cache is expired
        if (currentCache.lastFetched && (Date.now() - currentCache.lastFetched > CACHE_EXPIRY_TIME)) {
            // Only trigger fetch if not already fetching
            if (!currentIsFetching && !currentCache.isLoading) {
                console.log('ProfileDataCache: Cache expired, refreshing data...');
                fetchAllProfileData();
            } else {
                console.log('ProfileDataCache: Cache expired but fetch already in progress, skipping duplicate request');
            }
            // Return stale data while refreshing in background
            return currentCache[dataType];
        }

        return currentCache[dataType];
    }, [fetchAllProfileData]);

    const value: ProfileDataCacheContextType = {
        profileCache,
        refreshProfileData,
        clearCache,
        getCachedData,
    };

    return (
        <ProfileDataCacheContext.Provider value={value}>
            {children}
        </ProfileDataCacheContext.Provider>
    );
};

export const useProfileDataCache = (): ProfileDataCacheContextType => {
    const context = useContext(ProfileDataCacheContext);
    if (!context) {
        throw new Error('useProfileDataCache must be used within ProfileDataCacheProvider');
    }
    return context;
};
